import { LocalDate } from '@js-joda/core';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPropertyWeatherOptions } from '@helios/frontend-api-v2-client';
import { getWeekEnd, getWeekStart } from '../../utils/time';

import { TimePeriod } from '../energy/types';

import { ApiWeatherResponse, WeatherResponseData } from './types';

const STALE_TIME_TODAY = 0;
const STALE_TIME_PAST = 60 * 60 * 1000; // 60 minutes
const SNOW_DESCRIPTION_PATTERN = /snow|sleet|blizzard/i;

export function useWeather(startDate: LocalDate, endDate: LocalDate, propertyId?: string, enabled = true) {
  const isToday = endDate.isAfter(LocalDate.now().minusDays(1));
  return useQuery({
    ...getPropertyWeatherOptions({
      path: { id: propertyId ?? '' },
      query: { startDate: startDate.toString(), endDate: endDate.toString() },
      throwOnError: true,
    }),
    staleTime: isToday ? STALE_TIME_TODAY : STALE_TIME_PAST,
    enabled: enabled && Boolean(propertyId),
  });
}

export function useDailyWeatherHistory(
  endDate: LocalDate,
  period: TimePeriod,
  propertyId?: string,
  enabled = true,
): WeatherResponseData {
  let startDate: LocalDate;
  let adjustedEndDate: LocalDate = endDate;

  if (period === TimePeriod.week) {
    startDate = getWeekStart(endDate);
    const weekEnd = getWeekEnd(endDate);
    adjustedEndDate = weekEnd.isAfter(LocalDate.now()) ? LocalDate.now() : weekEnd;
  } else if (period === TimePeriod.month) {
    startDate = endDate.withDayOfMonth(1);

    adjustedEndDate = startDate.plusMonths(1).minusDays(1).isAfter(LocalDate.now())
      ? LocalDate.now()
      : startDate.plusMonths(1).minusDays(1);
  } else {
    startDate = endDate;
  }
  const { data, error, isLoading } = useWeather(startDate, adjustedEndDate, propertyId, enabled);

  const processedData = useMemo(() => {
    if (!enabled || !data) {
      return undefined;
    }
    return data.conditions.map((weather) => {
      const hourly = weather.hourly || [];
      return {
        timestamp: weather.date.split('T')[0],
        avg: weather.temperatureAverage,
        cloudCover: hourly.length
          ? Math.round(
              hourly.flatMap((hour) => hour.cloudCover).reduce((a, b) => a + b, 0) /
                hourly.length,
            )
          : 0,
        descriptions: hourly.flatMap((hour) => hour.description),
        max: weather.temperatureHigh,
        min: weather.temperatureLow,
      };
    });
  }, [data, enabled]);

  if (!enabled) {
    return {
      loading: false,
    };
  }

  return { loading: isLoading, error: error?.message, response: processedData };
}

const getTimeStampForHour = (dateStr: string, hour: number) => {
  const date = new Date(dateStr);
  date.setUTCHours(hour, 0, 0, 0);
  return date.toISOString().slice(0, -8); // eg 2024-03-28T22:00
};

export const toLocalHour = (utcIso: string, utcOffset: number): number =>
  ((new Date(utcIso).getUTCHours() * 60 + new Date(utcIso).getUTCMinutes()) / 60 + utcOffset + 24) % 24;

export const isNightHour = (localHour: number, sunrise: number, sunset: number): boolean =>
  localHour + 1 <= sunrise || localHour >= sunset;

export function useHourlyWeatherHistory(endDate: LocalDate, propertyId?: string, enabled = true): WeatherResponseData {
  const { data, error, isLoading } = useWeather(endDate, endDate, propertyId, enabled);

  const processedData = useMemo(() => {
    if (!enabled || !data) {
      return undefined;
    }
    return data.conditions.flatMap((weather) => {
      const hourly = weather.hourly || [];
      const sunriseLocalHour =
        weather.sunrise && weather.utcOffset !== undefined
          ? toLocalHour(weather.sunrise, weather.utcOffset)
          : undefined;
      const sunsetLocalHour =
        weather.sunset && weather.utcOffset !== undefined
          ? toLocalHour(weather.sunset, weather.utcOffset)
          : undefined;
      return hourly.map((hour) => {
        const localHour = hour.hour > 23 ? Math.floor(hour.hour / 100) : hour.hour;
        return {
          timestamp: getTimeStampForHour(weather.date, localHour),
          avg: hour.temperature,
          cloudCover: hour.cloudCover,
          temperature: hour.temperature,
          descriptions: hour.description,
          nighttime:
            sunriseLocalHour !== undefined && sunsetLocalHour !== undefined
              ? isNightHour(localHour, sunriseLocalHour, sunsetLocalHour)
              : localHour < 6 || localHour >= 18,
        };
      });
    });
  }, [data, enabled]);

  if (!enabled) {
    return {
      loading: false,
    };
  }

  const firstCondition = data?.conditions.find(
    (c) => c.sunrise && c.sunset && c.utcOffset !== undefined,
  );
  const sunriseLocalHour =
    firstCondition !== undefined
      ? toLocalHour(firstCondition.sunrise, firstCondition.utcOffset)
      : undefined;
  const sunsetLocalHour =
    firstCondition !== undefined
      ? toLocalHour(firstCondition.sunset, firstCondition.utcOffset)
      : undefined;

  return {
    loading: isLoading,
    error: error?.message,
    response: processedData,
    sunriseLocalHour,
    sunsetLocalHour,
  };
}

export const getIsSnowing = (weather: ApiWeatherResponse | undefined, currentHour: number): boolean =>
  weather?.hourly
    ?.find((hour) => Math.floor(hour.hour / 100) === currentHour)
    ?.description.some((description) => SNOW_DESCRIPTION_PATTERN.test(description)) ?? false;

export function useIsSnowing(propertyId?: string) {
  const { data, isLoading } = useWeather(LocalDate.now(), LocalDate.now(), propertyId);

  return { isSnowing: getIsSnowing(data?.conditions?.[0], new Date().getHours()), isLoading };
}
